import "./CreateArticleDraftAction.css";

import {
  AlertTriangle,
  FilePlus2,
  Link2,
} from "lucide-react";

import {
  useEffect,
  useState,
  type FormEvent,
} from "react";

import {
  createContentCalendarArticleDraft,
  type ContentCalendarItem,
} from "../lib/contentCalendar";

interface CreateArticleDraftActionProps {
  calendarItem:
    ContentCalendarItem;

  onUpdated:
    (
      calendarItem:
        ContentCalendarItem,
    ) => void;
}

interface ArticleDraftFormState {
  title:
    string;

  slug:
    string;

  summary:
    string;

  articleUrl:
    string;

  featuredImageUrl:
    string;

  featuredImageAltText:
    string;

  socialHook:
    string;

  callToAction:
    string;

  hashtags:
    string;

  notes:
    string;
}

function createSlugSuggestion(
  value:
    string,
): string {
  return value
    .trim()
    .toLowerCase()
    .replace(
      /['’]/g,
      "",
    )
    .replace(
      /[^a-z0-9]+/g,
      "-",
    )
    .replace(
      /^-+|-+$/g,
      "",
    )
    .slice(
      0,
      180,
    )
    .replace(
      /-+$/g,
      "",
    );
}

function createInitialForm(
  calendarItem:
    ContentCalendarItem,
): ArticleDraftFormState {
  const slug =
    createSlugSuggestion(
      calendarItem.workingTitle,
    );

  return {
    title:
      calendarItem.workingTitle,

    slug,

    summary:
      calendarItem.researchNotes ??
      "",

    articleUrl:
      slug
        ? `https://vextelecom.com/insights/${slug}`
        : "",

    featuredImageUrl:
      "",

    featuredImageAltText:
      "",

    socialHook:
      "",

    callToAction:
      calendarItem.preferredCta ??
      "Read the full article:",

    hashtags:
      "#VEXTelecom #Telecommunications #CustomerExperience",

    notes:
      "",
  };
}

function parseHashtags(
  value:
    string,
): string[] {
  return value
    .split(
      /[\s,]+/,
    )
    .map(
      (
        hashtag,
      ) =>
        hashtag.trim(),
    )
    .filter(
      Boolean,
    );
}

function CreateArticleDraftAction({
  calendarItem,
  onUpdated,
}: CreateArticleDraftActionProps) {
  const [
    isConfirming,
    setIsConfirming,
  ] =
    useState(
      false,
    );

  const [
    form,
    setForm,
  ] =
    useState<
      ArticleDraftFormState
    >(
      () =>
        createInitialForm(
          calendarItem,
        ),
    );

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(
      false,
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  useEffect(
    () => {
      setIsConfirming(
        false,
      );

      setIsSubmitting(
        false,
      );

      setError(
        null,
      );

      setForm(
        createInitialForm(
          calendarItem,
        ),
      );
    },
    [
      calendarItem.id,
    ],
  );

  if (
    calendarItem.status !==
      "preparation_ready" ||
    calendarItem.articleId !==
      null
  ) {
    return null;
  }

  const cancelConfirmation =
    () => {
      setError(
        null,
      );

      setForm(
        createInitialForm(
          calendarItem,
        ),
      );

      setIsConfirming(
        false,
      );
    };

  const submitDraft =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ) => {
      event.preventDefault();

      setError(
        null,
      );

      const requiredFields = [
        [
          "Article title",
          form.title,
        ],
        [
          "Article slug",
          form.slug,
        ],
        [
          "Summary",
          form.summary,
        ],
        [
          "Article URL",
          form.articleUrl,
        ],
        [
          "Featured image URL",
          form.featuredImageUrl,
        ],
        [
          "Featured image alt text",
          form.featuredImageAltText,
        ],
      ] as const;

      const missingField =
        requiredFields.find(
          (
            [
              ,
              value,
            ],
          ) =>
            !value.trim(),
        );

      if (
        missingField
      ) {
        setError(
          `${missingField[0]} is required.`,
        );

        return;
      }

      setIsSubmitting(
        true,
      );

      try {
        const response =
          await createContentCalendarArticleDraft({
            calendarItemId:
              calendarItem.id,

            notes:
              form.notes,

            article: {
              title:
                form.title,

              slug:
                form.slug,

              summary:
                form.summary,

              articleUrl:
                form.articleUrl,

              featuredImageUrl:
                form.featuredImageUrl,

              featuredImageAltText:
                form.featuredImageAltText,

              socialHook:
                form.socialHook,

              callToAction:
                form.callToAction,

              hashtags:
                parseHashtags(
                  form.hashtags,
                ),
            },
          });

        onUpdated(
          response.calendarItem,
        );
      } catch (
        creationError
      ) {
        setError(
          creationError instanceof
            Error
            ? creationError.message
            : "The article draft could not be created.",
        );
      } finally {
        setIsSubmitting(
          false,
        );
      }
    };

  if (
    !isConfirming
  ) {
    return (
      <section className="calendar-workflow-action calendar-article-action">
        <div className="calendar-workflow-action-copy">
          <span className="calendar-workflow-icon calendar-article-icon">
            <FilePlus2
              aria-hidden
              size={18}
              strokeWidth={1.8}
            />
          </span>

          <div>
            <h3>
              Article draft workflow
            </h3>

            <p>
              Create a new draft from this preparation-ready record and
              link both records through one controlled transaction.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="primary-button"
          onClick={() => {
            setError(
              null,
            );

            setIsConfirming(
              true,
            );
          }}
        >
          <FilePlus2
            aria-hidden
            size={16}
            strokeWidth={1.9}
          />

          Create article draft
        </button>
      </section>
    );
  }

  return (
    <section className="calendar-workflow-confirmation calendar-article-confirmation">
      <header>
        <span className="calendar-workflow-icon calendar-article-icon">
          <Link2
            aria-hidden
            size={18}
            strokeWidth={1.8}
          />
        </span>

        <div>
          <h3>
            Create and link article draft
          </h3>

          <p>
            This creates a new
            <strong> Draft </strong>
            article and changes the calendar record from
            <strong> Preparation ready </strong>
            to
            <strong> Article linked</strong>.
          </p>
        </div>
      </header>

      {error ? (
        <div
          className="calendar-workflow-error"
          role="alert"
        >
          <AlertTriangle
            aria-hidden
            size={17}
            strokeWidth={1.9}
          />

          <span>
            {error}
          </span>
        </div>
      ) : null}

      <form
        className="calendar-article-form"
        onSubmit={submitDraft}
      >
        <div className="calendar-article-form-grid">
          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Article title
            </span>

            <input
              required
              maxLength={180}
              value={form.title}
              disabled={isSubmitting}
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    title:
                      event.target.value,
                  }),
                );
              }}
            />

            <small>
              {form.title.length}/180
            </small>
          </label>

          <label className="calendar-article-field">
            <span>
              Slug
            </span>

            <input
              required
              maxLength={180}
              value={form.slug}
              disabled={isSubmitting}
              placeholder="article-url-slug"
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    slug:
                      event.target.value,
                  }),
                );
              }}
            />
          </label>

          <label className="calendar-article-field">
            <span>
              Article URL
            </span>

            <input
              required
              type="url"
              value={form.articleUrl}
              disabled={isSubmitting}
              placeholder="https://vextelecom.com/insights/..."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    articleUrl:
                      event.target.value,
                  }),
                );
              }}
            />
          </label>

          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Summary
            </span>

            <textarea
              required
              rows={5}
              maxLength={900}
              value={form.summary}
              disabled={isSubmitting}
              placeholder="Summarize the article’s purpose, argument and business value."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    summary:
                      event.target.value,
                  }),
                );
              }}
            />

            <small>
              {form.summary.length}/900
            </small>
          </label>

          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Featured image URL
            </span>

            <input
              required
              type="url"
              value={form.featuredImageUrl}
              disabled={isSubmitting}
              placeholder="https://vextelecom.com/images/insights/..."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    featuredImageUrl:
                      event.target.value,
                  }),
                );
              }}
            />

            {calendarItem.featuredImageRequirements ? (
              <em>
                Requirement: {calendarItem.featuredImageRequirements}
              </em>
            ) : null}
          </label>

          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Featured image alt text
            </span>

            <textarea
              required
              rows={3}
              maxLength={300}
              value={form.featuredImageAltText}
              disabled={isSubmitting}
              placeholder="Describe the image accurately for accessibility and search."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    featuredImageAltText:
                      event.target.value,
                  }),
                );
              }}
            />

            <small>
              {form.featuredImageAltText.length}/300
            </small>
          </label>

          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Social hook
            </span>

            <textarea
              rows={3}
              maxLength={300}
              value={form.socialHook}
              disabled={isSubmitting}
              placeholder="Optional topic-specific opening for social promotion."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    socialHook:
                      event.target.value,
                  }),
                );
              }}
            />

            <small>
              {form.socialHook.length}/300
            </small>
          </label>

          <label className="calendar-article-field">
            <span>
              Call to action
            </span>

            <input
              maxLength={180}
              value={form.callToAction}
              disabled={isSubmitting}
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    callToAction:
                      event.target.value,
                  }),
                );
              }}
            />
          </label>

          <label className="calendar-article-field">
            <span>
              Hashtags
            </span>

            <input
              value={form.hashtags}
              disabled={isSubmitting}
              placeholder="#VEXTelecom #Telecommunications"
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    hashtags:
                      event.target.value,
                  }),
                );
              }}
            />
          </label>

          <label className="calendar-article-field calendar-article-field-wide">
            <span>
              Audit notes
            </span>

            <textarea
              rows={4}
              maxLength={2_000}
              value={form.notes}
              disabled={isSubmitting}
              placeholder="Optional: record the review, research and approval context for this draft."
              onChange={(
                event,
              ) => {
                setForm(
                  (
                    current,
                  ) => ({
                    ...current,

                    notes:
                      event.target.value,
                  }),
                );
              }}
            />

            <small>
              {form.notes.length}/2000
            </small>
          </label>
        </div>

        <footer className="calendar-article-footer">
          <span>
            This operation creates a production article record and cannot
            be repeated for the same calendar item.
          </span>

          <div>
            <button
              type="button"
              className="secondary-button"
              disabled={isSubmitting}
              onClick={cancelConfirmation}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting}
            >
              <FilePlus2
                aria-hidden
                size={16}
                strokeWidth={1.9}
              />

              {isSubmitting
                ? "Creating draft..."
                : "Create and link draft"}
            </button>
          </div>
        </footer>
      </form>
    </section>
  );
}

export default CreateArticleDraftAction;
